import React, { lazy, Suspense, useEffect } from 'react'
import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Navigate,
} from 'react-router-dom'
import { useSelector } from 'react-redux'
import * as microsoftTeams from '@microsoft/teams-js'
import styled from 'styled-components'
import Icons from '../assets/Icons'
import HomeSkeleton from '../components/Skeletons/HomeSkeleton'

// 🎯 Lazy load all heavy components
const Home = lazy(() => import('../container/Home'))
const Login = lazy(() => import('../container/Login'))
const PdfViewer = lazy(() => import('../container/PdfViewer/edit'))
const SignPdfViewer = lazy(() => import('../container/PdfViewer/sign'))
const Header = lazy(() => import('../layout/Header'))
// const Document = lazy(() => import('../container/Document'))
const PdfPreview = lazy(() => import('../components/PdfPreview'))
const PageNotFound = lazy(() => import('../container/PageNotFound'))
const OrganizePdf = lazy(() => import('../container/OrganizePdf'))
const TeamsAuthorize = lazy(() => import('../container/TeamsAuthorize'))
const JobHistory = lazy(() => import('../container/JobHistory'))
const RedactEdit = lazy(() => import('../container/RedactEdit'))
const DocumentsRoutes = lazy(() => import('./DocumentRoute'))
const AllTools = lazy(() => import('../container/AllTools'))

const SpinnerWrapper = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
`
const Loader = () => (
  <SpinnerWrapper>
    <Icons toolName={'squareLoader'} />
  </SpinnerWrapper>
)

const Router = () => {
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route>
        <Route path="/" errorElement={<PageNotFound />}>
          {/* PUBLIC ROUTE */}
          <Route
            path="need-consent"
            element={
              <Suspense fallback={<Loader />}>
                <TeamsAuthorize />
              </Suspense>
            }
          />

          {/* PROTECTED AREA */}
          <Route
            element={
              <Suspense fallback={<Loader />}>
                <Header />
              </Suspense>
            }
          >
            {/* HOME */}
            <Route
              index
              path="home"
              element={
                <Suspense fallback={<HomeSkeleton />}>
                  <Home />
                </Suspense>
              }
            />
            <Route
              index
              path="all-tools"
              element={
                <Suspense fallback={<Loader />}>
                  <AllTools />
                </Suspense>
              }
            />

            {/* DOCUMENT SECTION */}
            <Route
              path="document/*"
              element={
                <Suspense fallback={<Loader />}>
                  <DocumentsRoutes />
                </Suspense>
              }
            />

            {/* LOGIN */}
            <Route
              path="login"
              element={
                <Suspense fallback={<Loader />}>
                  <Login />
                </Suspense>
              }
            />

            {/* JOB HISTORY */}
            <Route
              path="job-history"
              element={
                <Suspense fallback={<Loader />}>
                  <JobHistory />
                </Suspense>
              }
            />

            {/* PDF VIEWERS */}
            <Route
              path="viewer"
              element={
                <Suspense fallback={<Loader />}>
                  <PdfViewer />
                </Suspense>
              }
            />

            <Route
              path="sign-viewer"
              element={
                <Suspense fallback={<Loader />}>
                  <SignPdfViewer />
                </Suspense>
              }
            />

            {/* PDF PREVIEW */}
            <Route
              path="preview"
              element={
                <Suspense fallback={<Loader />}>
                  <PdfPreview />
                </Suspense>
              }
            />

            {/* ORGANIZE PDF */}
            <Route
              path="OrganizePdf"
              element={
                <Suspense fallback={<Loader />}>
                  <OrganizePdf />
                </Suspense>
              }
            />

            {/* REDACT EDIT */}
            <Route
              path="redact-edit"
              element={
                <Suspense fallback={<Loader />}>
                  <RedactEdit />
                </Suspense>
              }
            />
          </Route>

          {/* CATCH ALL → Redirect to home */}
          <Route path="*" element={<Navigate to="/" />} />
        </Route>

        {/* PAGE NOT FOUND */}
        <Route
          path="/404"
          element={
            <Suspense fallback={<Loader />}>
              <PageNotFound />
            </Suspense>
          }
        />
      </Route>
    )
  )

  return <RouterProvider router={router} />
}

export default Router
